import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { db } from '@/lib/firebase/config';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, Timestamp } from 'firebase/firestore';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { IconPicker, IconDisplay } from '@/components/ui/icon-picker';

interface Statistic {
  id: string;
  number: string;
  label: { ar: string; en: string };
  icon: string;
  order: number;
}

export function StatisticsSection() {
  const [statistics, setStatistics] = useState<Statistic[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isIconPickerOpen, setIsIconPickerOpen] = useState(false);
  const [currentStat, setCurrentStat] = useState<Statistic | null>(null);
  const [formData, setFormData] = useState({
    number: '',
    label: { ar: '', en: '' },
    icon: 'Users',
  });

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      const statsQuery = query(
        collection(db, 'landing_statistics'),
        orderBy('order', 'asc')
      );
      const querySnapshot = await getDocs(statsQuery);

      const statsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Statistic[];

      setStatistics(statsData);
    } catch (err) {
      console.error('Error fetching statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (stat?: Statistic) => {
    if (stat) {
      setCurrentStat(stat);
      setFormData({
        number: stat.number,
        label: typeof stat.label === 'string' ? { ar: stat.label, en: '' } : stat.label,
        icon: stat.icon,
      });
    } else {
      setCurrentStat(null);
      setFormData({
        number: '',
        label: { ar: '', en: '' },
        icon: 'Users',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentStat(null);
    setFormData({
      number: '',
      label: { ar: '', en: '' },
      icon: 'Users',
    });
  };

  const handleSave = async () => {
    try {
      if (currentStat) {
        // Update existing statistic
        const statRef = doc(db, 'landing_statistics', currentStat.id);
        await updateDoc(statRef, {
          number: formData.number,
          label: formData.label,
          icon: formData.icon,
          updatedAt: Timestamp.now(),
        });
      } else {
        // Add new statistic
        await addDoc(collection(db, 'landing_statistics'), {
          number: formData.number,
          label: formData.label,
          icon: formData.icon,
          order: statistics.length,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
      }
      await fetchStatistics();
      handleCloseModal();
    } catch (err) {
      console.error('Error saving statistic:', err);
    }
  };

  const handleDelete = async (statId: string) => {
    if (!confirm('هل أنت متأكد من حذف هذه الإحصائية؟')) return;

    try {
      await deleteDoc(doc(db, 'landing_statistics', statId));
      await fetchStatistics();
    } catch (err) {
      console.error('Error deleting statistic:', err);
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;

    const newStats = [...statistics];
    [newStats[index - 1], newStats[index]] = [newStats[index], newStats[index - 1]];

    await updateOrderInFirebase(newStats);
  };

  const handleMoveDown = async (index: number) => {
    if (index === statistics.length - 1) return;

    const newStats = [...statistics];
    [newStats[index], newStats[index + 1]] = [newStats[index + 1], newStats[index]];

    await updateOrderInFirebase(newStats);
  };

  const updateOrderInFirebase = async (orderedStats: Statistic[]) => {
    try {
      const updatePromises = orderedStats.map((stat, index) =>
        updateDoc(doc(db, 'landing_statistics', stat.id), {
          order: index,
          updatedAt: Timestamp.now(),
        })
      );
      await Promise.all(updatePromises);
      await fetchStatistics();
    } catch (err) {
      console.error('Error updating order:', err);
    }
  };

  if (loading) {
    return <Card className="p-6">جاري التحميل...</Card>;
  }

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold">إدارة الإحصائيات</h3>
        <Button onClick={() => handleOpenModal()}>
          <Plus className="w-4 h-4 ml-2" />
          إضافة إحصائية
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statistics.map((stat, index) => (
          <Card key={stat.id} className="p-4 bg-[#7dda55]">
            <div className="text-center">
              <IconDisplay iconName={stat.icon} className="w-12 h-12 text-white mx-auto mb-4" />
              <div className="text-3xl font-bold text-[#004250] mb-2">{stat.number}</div>
              <div className="text-white text-lg mb-4">{typeof stat.label === 'string' ? stat.label : stat.label.ar}</div>
            </div>
            <div className="flex gap-2 justify-center">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleMoveUp(index)}
                disabled={index === 0}
                className="bg-white"
              >
                ↑
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleMoveDown(index)}
                disabled={index === statistics.length - 1}
                className="bg-white"
              >
                ↓
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleOpenModal(stat)}
                className="bg-white"
              >
                <Pencil className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleDelete(stat.id)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {currentStat ? 'تعديل إحصائية' : 'إضافة إحصائية جديدة'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="number">الرقم</Label>
              <Input
                id="number"
                value={formData.number}
                onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                placeholder="مثال: 10,000+"
              />
            </div>

            <div className="space-y-3">
              <Label className="text-base font-semibold">الوصف / Label</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="label-ar" className="text-sm text-muted-foreground">عربي</Label>
                  <Input
                    id="label-ar"
                    value={formData.label.ar}
                    onChange={(e) => setFormData({ ...formData, label: { ...formData.label, ar: e.target.value } })}
                    placeholder="مثال: مستفيد"
                    dir="rtl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="label-en" className="text-sm text-muted-foreground">English</Label>
                  <Input
                    id="label-en"
                    value={formData.label.en}
                    onChange={(e) => setFormData({ ...formData, label: { ...formData.label, en: e.target.value } })}
                    placeholder="e.g. Beneficiary"
                    dir="ltr"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label>الأيقونة</Label>
              <div className="flex gap-2 items-center">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsIconPickerOpen(true)}
                  className="w-full justify-start"
                >
                  <IconDisplay iconName={formData.icon} className="w-5 h-5 ml-2" />
                  {formData.icon}
                </Button>
              </div>
            </div>

            <div className="p-4 bg-[#7dda55] rounded-lg text-center">
              <IconDisplay iconName={formData.icon} className="w-12 h-12 text-white mx-auto mb-2" />
              <div className="text-2xl font-bold text-[#004250] mb-1">{formData.number || '0'}</div>
              <div className="text-white">{formData.label.ar || 'معاينة'}</div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseModal}>
              إلغاء
            </Button>
            <Button onClick={handleSave}>
              حفظ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <IconPicker
        open={isIconPickerOpen}
        value={formData.icon}
        onChange={(iconName) => setFormData({ ...formData, icon: iconName })}
        onClose={() => setIsIconPickerOpen(false)}
      />
    </Card>
  );
}

